import { DestroyRef, Injectable } from '@angular/core'
import { CasinoGamesWsApiWsApi, CasinoVirtualSubCategory } from '@/api/websocket/casino-games-ws-api'
import { AuthService } from '@/services/auth.service'
import { LayoutService } from '@/services/layout.service'
import { CASINO_CATEGORIES, CasinoGameItem } from '@/features/casino/casino.model'
import { toCasinoGameItems } from '@/features/casino/casino.mapper'
import { SearchView } from '../search.model'
import { toCasinoSearchView } from '../casino-search.mapper'
import { SearchSource } from '../search.source'

// The API's ceiling per page. Search shows the first page only; paging is the
// casino filter's, which answers with the same list.
const SEARCH_PAGE_SIZE = 20

/**
 * Casino games, through the API's search call rather than the lobby's gameName
 * filter: search is its own list now, not a narrowed lobby. It takes no
 * category, so a game is found from any casino section.
 */
@Injectable({ providedIn: 'root' })
export class CasinoSearchSource implements SearchSource {
	constructor(
		private casinoGamesWsApiWsApi: CasinoGamesWsApiWsApi,
		private layoutService: LayoutService,
		private authService: AuthService,
		private destroyRef: DestroyRef
	) {}

	// Kept until reload, per device type and login state: recommendations are the player's own.
	private readonly suggested = new Map<string, Promise<CasinoGameItem[]>>()

	/**
	 * Suggested Games for the panel's empty state: the main casino's Recommended
	 * row. Empty when the visitor has none or the call fails; the row then hides.
	 */
	public suggestedGames(): Promise<CasinoGameItem[]> {
		const mobile = this.layoutService.isMobile()
		const key = `${mobile}-${this.authService.isLoggedIn}`
		let pending = this.suggested.get(key)

		if (!pending) {
			pending = this.casinoGamesWsApiWsApi.getCasinoGamesByVirtualSubCategory({
				gcId: CASINO_CATEGORIES.casino,
				gsId: CasinoVirtualSubCategory.recommended,
				mobile,
				fromIndex: 0
			}, this.destroyRef)
				.then((category) => toCasinoGameItems(
					category.subCategories?.find((subCategory) =>
						subCategory.gsId === CasinoVirtualSubCategory.recommended)?.games ?? []
				))
				.catch(() => [])
				.then((games) => {
					// An empty list or a failure isn't kept, so the next opening asks again.
					if (games.length === 0) this.suggested.delete(key)
					return games
				})
			this.suggested.set(key, pending)
		}

		return pending
	}

	public async search(term: string): Promise<SearchView> {
		const result = await this.casinoGamesWsApiWsApi.searchCasinoGames({
			mobile: this.layoutService.isMobile(),
			searchText: term,
			fromIndex: 0,
			pageSize: SEARCH_PAGE_SIZE
		}, this.destroyRef)

		return toCasinoSearchView(result)
	}
}
